import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticsService } from '../../../services/analytics.service';

interface DateRange {
  label: string;
  value: number;
}

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css']
})
export class AnalyticsComponent implements OnInit {
  dateRanges: DateRange[] = [
    { label: '1 Day', value: 1 },
    { label: '3 Days', value: 3 },
    { label: '7 Days', value: 7 },
    { label: '14 Days', value: 14 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 }
  ];

  selectedRange: number = 7;
  loading: boolean = false;
  error: string = '';

  summary: any = {
    today: {
      total_conversations: 0,
      total_messages: 0,
      faq_matches: 0,
      ai_responses: 0,
      avg_sentiment: 0
    },
    totals: {
      total_conversations: 0,
      total_messages: 0,
      faq_matches: 0,
      ai_responses: 0
    }
  };

  trends: any[] = [];

  // Computed stats
  get rangeTotals(): any {
    if (!this.trends || this.trends.length === 0) return null;
    return this.trends.reduce((acc, curr) => ({
      total_conversations: acc.total_conversations + (Number(curr.total_conversations) || 0),
      total_messages: acc.total_messages + (Number(curr.total_messages) || 0),
      faq_matches: acc.faq_matches + (Number(curr.faq_matches) || 0),
      ai_responses: acc.ai_responses + (Number(curr.ai_responses) || 0),
      avg_sentiment_score: acc.avg_sentiment_score + (Number(curr.avg_sentiment_score) || 0),
      count: acc.count + 1
    }), { total_conversations: 0, total_messages: 0, faq_matches: 0, ai_responses: 0, avg_sentiment_score: 0, count: 0 });
  }

  get rangeAvgSentiment(): number {
    const totals = this.rangeTotals;
    return totals && totals.count > 0 ? totals.avg_sentiment_score / totals.count : 0;
  }

  get faqEffectiveness(): number {
    const source = this.selectedRange === 1 ? this.summary.today : this.rangeTotals;
    if (!source) return 0;
    const total = (Number(source.faq_matches) || 0) + (Number(source.ai_responses) || 0);
    return total > 0 ? Math.round(((Number(source.faq_matches) || 0) / total) * 100) : 0;
  }

  get avgMessagesPerConvo(): number {
    const source = this.selectedRange === 1 ? this.summary.today : this.rangeTotals;
    if (!source) return 0;
    return (Number(source.total_conversations) || 0) > 0
      ? Math.round(((Number(source.total_messages) || 0) / (Number(source.total_conversations) || 0)) * 10) / 10
      : 0;
  }

  get sentimentLabel(): string {
    const score = this.selectedRange === 1 ? (this.summary.today.avg_sentiment_score || 0) : this.rangeAvgSentiment;
    if (score >= 0.75) return 'Very Positive';
    if (score >= 0.6) return 'Positive';
    if (score >= 0.4) return 'Neutral';
    if (score >= 0.2) return 'Negative';
    return 'Very Negative';
  }

  get sentimentColor(): string {
    const score = this.selectedRange === 1 ? (this.summary.today.avg_sentiment_score || 0) : this.rangeAvgSentiment;
    if (score >= 0.75) return 'text-emerald-600 font-bold';
    if (score >= 0.6) return 'text-green-600 font-semibold';
    if (score >= 0.4) return 'text-amber-600';
    return 'text-red-600';
  }

  math = Math;

  constructor(
    private analyticsService: AnalyticsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadAnalytics();
  }

  loadAnalytics(): void {
    this.loading = true;
    this.error = '';

    // Load summary
    this.analyticsService.getSummary().subscribe({
      next: (data) => {
        this.summary = data;
        console.log('Analytics summary loaded:', data);
      },
      error: (err) => {
        console.error('Failed to load summary:', err);
        this.error = 'Failed to load analytics data';
      }
    });

    // Load trends
    this.analyticsService.getTrends(this.selectedRange).subscribe({
      next: (data) => {
        // FIX: The API returns { success: true, data: [...] }
        this.trends = data.data || [];
        console.log('Analytics trends loaded:', this.trends);
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load trends:', err);
        this.error = 'Failed to load trends data';
        this.loading = false;
      }
    });
  }

  onRangeChange(): void {
    this.loadAnalytics();
  }

  exportData(): void {
    const exportData = {
      summary: this.summary,
      trends: this.trends,
      exportDate: new Date().toISOString(),
      dateRange: this.selectedRange
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${this.selectedRange}days-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  exportPdf(): void {
    const jsPDF = (window as any).jspdf.jsPDF;
    const doc = new jsPDF();

    // Header
    doc.setFontSize(22);
    doc.setTextColor(110, 68, 255); // Purple
    doc.text('JoyBot Analytics Report', 14, 22);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
    doc.text(`Time Range: Last ${this.selectedRange} Days`, 14, 36);

    // Stats Table
    doc.setFontSize(16);
    doc.setTextColor(33, 37, 41);
    const summaryTitle = this.selectedRange === 1 ? 'Summary Metrics (Today)' : `Summary Metrics (Last ${this.selectedRange} Days)`;
    doc.text(summaryTitle, 14, 50);

    const source = this.selectedRange === 1 ? this.summary.today : this.rangeTotals;
    const avgSent = this.selectedRange === 1 ? (this.summary.today.avg_sentiment_score || 0) : this.rangeAvgSentiment;

    const summaryData = [
      ['Total Conversations', source?.total_conversations || 0],
      ['Total Messages', source?.total_messages || 0],
      ['FAQ Matches', source?.faq_matches || 0],
      ['AI Responses', source?.ai_responses || 0],
      ['Avg. Sentiment', `${Math.round((avgSent || 0) * 100)}%`]
    ];

    (doc as any).autoTable({
      startY: 55,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [110, 68, 255] }
    });

    // Trends Table
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    doc.text('Daily Trends Data', 14, finalY);

    const trendRows = this.trends.map(t => [
      t.date,
      t.total_conversations,
      t.total_messages,
      t.faq_matches,
      t.ai_responses,
      `${Math.round((t.avg_sentiment_score || 0) * 100)}%`
    ]);

    (doc as any).autoTable({
      startY: finalY + 5,
      head: [['Date', 'Convs', 'Msgs', 'FAQ', 'AI', 'Sentiment']],
      body: trendRows,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });

    doc.save(`joybot-report-${this.selectedRange}days-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }
}
